import type { JSX } from 'preact';
import { useRef, useEffect } from 'preact/hooks';
import cn from 'classnames';
import type { MediaGalleryConfig } from '../../MediaGallery.config.ts';
import type { MediaGalleryItem } from '../../MediaGallery.tsx';
import { getImageLazyLoadingProp } from '../../MediaGallery.utils.ts';
import styles from './Thumbnails.module.scss';
import ThumbnailItem from '../ThumbnailItem/ThumbnailItem.tsx';
import { lastEventSource } from '@/widgets/hooks/useVideoTracking.tsx';

/** Agreed with Designer that this visually makes sense */
const MAX_THUMBNAILS_BEFORE_LEFT_ALIGN = 5;

export type ThumbnailsProps = {
  items: MediaGalleryItem[];
  selectedIndex: number;
  config: MediaGalleryConfig;
  isFullscreen?: boolean;
  class?: string;
  onThumbnailClick: (
    event: JSX.TargetedEvent<HTMLButtonElement>,
    clickedIndex: number
  ) => void;
};
/** Note: Thumbnails are used on the carousel and full screen! */
export default function Thumbnails({
  items,
  selectedIndex,
  config,
  isFullscreen = false,
  class: className,
  onThumbnailClick
}: ThumbnailsProps) {
  const ulRef = useRef<HTMLUListElement>(null);
  const {
    lazyLoading,
    translations: { imageLabel }
  } = config;
  const imageLoading = lazyLoading?.thumb ?? 'auto';

  const scrollThumbnailTrackIntoView = () => {
    if (!ulRef.current) return;
    ulRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
  };

  const scrollToThumbnail = (index: number) => {
    if (!ulRef.current) return;
    const liElement = ulRef?.current.querySelector(
      `li:nth-child(${index - 1})`
    );
    liElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
  };

  const handleClick = (
    event: JSX.TargetedEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (!ulRef.current) return;
    event.preventDefault();
    lastEventSource.value = 'thumbnail click';
    scrollThumbnailTrackIntoView();
    scrollToThumbnail(index);
    onThumbnailClick(event, index);
  };

  useEffect(() => {
    scrollToThumbnail(selectedIndex);
  }, [selectedIndex]);

  return (
    <div class={cn(styles.container, className)}>
      <ul
        class={cn(styles.list, {
          [styles.fullscreen]: isFullscreen,
          [styles.listMany]: items.length > MAX_THUMBNAILS_BEFORE_LEFT_ALIGN
        })}
        ref={ulRef}
      >
        {items.map((item, index) => {
          // always lazy load images when fullscreen
          const loading = isFullscreen
            ? 'lazy'
            : getImageLazyLoadingProp(imageLoading, index, 0);
          return (
            <ThumbnailItem
              item={item}
              index={index}
              loading={loading}
              selected={index === selectedIndex}
              a11yLabel={item.alt || imageLabel}
              onThumbnailClick={(event, clickedIndex) =>
                handleClick(event, clickedIndex)
              }
              variant={isFullscreen ? 'fullscreen' : 'carousel'}
            />
          );
        })}
      </ul>
    </div>
  );
}
